import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, LOGIN_SCOPES } from './msalConfig';
import { SignIn } from './SignIn';

type AuthState = {
  isAuthenticated: boolean;
  user: { displayName: string; email: string } | null;
  getToken: () => Promise<string | null>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Called by useApi when the API returns 401, or by getToken when MSAL
   * cannot recover the session. Drops the user back to the SignIn screen
   * with a message instead of leaving the UI stuck in a broken state.
   */
  notifyAuthFailure: () => void;
  /**
   * "View as" preview state. When set, every API call attaches an X-Act-As
   * header — the API swaps req.user to that target and rejects mutations.
   * In-memory only (no localStorage) so a refresh always exits preview.
   */
  previewUserId: string | null;
  previewUser: { id: string; displayName: string; email: string } | null;
  startPreview: (target: { id: string; displayName: string; email: string }) => void;
  exitPreview: () => void;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

const noopPreview = {
  previewUserId: null,
  previewUser: null,
  startPreview: () => {},
  exitPreview: () => {},
};

const DEV_AUTH: AuthState = {
  isAuthenticated: true,
  user: { displayName: 'Dev Admin', email: 'dev-admin@alphadirect.co.bw' },
  getToken: async () => null,
  signIn: async () => {},
  signOut: async () => {},
  notifyAuthFailure: () => {},
  ...noopPreview,
};

const DevAuthProvider = ({ children }: { children: ReactNode }) => {
  const [previewUser, setPreviewUser] = useState<AuthState['previewUser']>(null);
  const startPreview = useCallback(
    (target: NonNullable<AuthState['previewUser']>) => setPreviewUser(target),
    [],
  );
  const exitPreview = useCallback(() => setPreviewUser(null), []);

  const value = useMemo<AuthState>(
    () => ({
      ...DEV_AUTH,
      previewUserId: previewUser?.id ?? null,
      previewUser,
      startPreview,
      exitPreview,
    }),
    [previewUser, startPreview, exitPreview],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AzureAuthInner = ({ children }: { children: ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  const [previewUser, setPreviewUser] = useState<AuthState['previewUser']>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const notifyAuthFailure = useCallback(() => {
    setSessionExpired(true);
  }, []);

  const clearLocalSession = useCallback(async () => {
    // Drop MSAL's cached account so the next sign-in is clean.
    if (account) {
      try {
        await instance.clearCache({ account });
      } catch {
        // Ignore — we'll still surface SignIn UI.
      }
    }
  }, [instance, account]);

  const signIn = useCallback(async () => {
    setSessionExpired(false);
    await instance.loginPopup({
      scopes: LOGIN_SCOPES,
      prompt: 'select_account',
    });
  }, [instance]);

  const signOut = useCallback(async () => {
    if (!account) return;
    try {
      await instance.logoutPopup({ account });
    } catch {
      // Popup blocked / closed by user — local state is cleared regardless.
    }
  }, [instance, account]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: LOGIN_SCOPES,
      });
      return result.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        try {
          const result = await instance.acquireTokenPopup({
            account,
            scopes: LOGIN_SCOPES,
          });
          return result.idToken;
        } catch {
          // Popup blocked, dismissed, or sign-in needed — drop to SignIn.
          await clearLocalSession();
          setSessionExpired(true);
          return null;
        }
      }
      // Network or unexpected — flag session and let user re-auth.
      await clearLocalSession();
      setSessionExpired(true);
      return null;
    }
  }, [instance, account, clearLocalSession]);

  const startPreview = useCallback(
    (target: NonNullable<AuthState['previewUser']>) => setPreviewUser(target),
    [],
  );
  const exitPreview = useCallback(() => setPreviewUser(null), []);

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: !!account && !sessionExpired,
      user: account
        ? {
            displayName: account.name ?? account.username,
            email: account.username,
          }
        : null,
      getToken,
      signIn,
      signOut,
      notifyAuthFailure,
      previewUserId: previewUser?.id ?? null,
      previewUser,
      startPreview,
      exitPreview,
    }),
    [
      account,
      sessionExpired,
      getToken,
      signIn,
      signOut,
      notifyAuthFailure,
      previewUser,
      startPreview,
      exitPreview,
    ],
  );

  // Initial MSAL handshake (reading sessionStorage cache, completing redirect, etc.)
  if (inProgress === 'startup' || inProgress === 'handleRedirect') {
    return (
      <div
        className="grid min-h-screen place-items-center bg-bg-tertiary text-sm text-text-tertiary"
        aria-busy="true"
      >
        Loading…
      </div>
    );
  }

  if (!account || sessionExpired) {
    return (
      <AuthContext.Provider value={value}>
        <SignIn
          onSignIn={signIn}
          message={
            sessionExpired
              ? 'Your session expired. Please sign in again.'
              : null
          }
        />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AzureAuthProvider = ({ children }: { children: ReactNode }) => (
  <MsalProvider instance={msalInstance}>
    <AzureAuthInner>{children}</AzureAuthInner>
  </MsalProvider>
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const mode = import.meta.env.VITE_AUTH_MODE ?? 'dev';
  if (mode === 'azure') return <AzureAuthProvider>{children}</AzureAuthProvider>;
  return <DevAuthProvider>{children}</DevAuthProvider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
